import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Shield, Loader2, CheckCircle, XCircle, AlertTriangle, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";

const RevertEmailChange = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading, confirm, reverting, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [revertResult, setRevertResult] = useState(null);
  const navigate = useNavigate();

  const checkToken = useCallback(async () => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No revert token found');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/v2/auth/account/check-revert-token/${token}`);
      if (response.data.valid) {
        setTokenInfo({
          currentEmail: response.data.currentEmail,
          originalEmail: response.data.originalEmail
        });
        setStatus('confirm');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Invalid or expired revert link');
    }
  }, [token]);

  useEffect(() => {
    checkToken();
  }, [checkToken]);

  const handleRevert = async () => {
    setStatus('reverting');

    try {
      const response = await axios.post(`${API_URL}/v2/auth/account/revert-email-change`, {
        token
      });

      if (response.data.success) {
        setRevertResult(response.data);
        setStatus('success');
        toast.success('Email reverted successfully!');
      }
    } catch (error) {
      setStatus('error');
      const errorMsg = error.response?.data?.detail || 'Failed to revert email change';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <Card className="w-full max-w-md shadow-xl rounded-xl">
          <CardHeader className="text-center py-12">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-xl shadow-lg bg-blue-600">
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              </div>
            </div>
            <CardTitle className="text-xl font-bold text-gray-900">
              Checking Link...
            </CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Confirmation state
  if (status === 'confirm' && tokenInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <Card className="w-full max-w-md shadow-xl rounded-xl">
          <CardHeader className="text-center pt-8 pb-4">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-xl shadow-lg bg-red-500">
                <AlertTriangle className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-xl font-bold text-gray-900">
              Revert Email Change
            </CardTitle>
            <p className="text-gray-600 mt-2">
              Someone changed your account email without your permission?
            </p>
          </CardHeader>

          <CardContent className="px-6 pb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 mb-3">This will:</p>
              <ul className="space-y-2 text-sm text-red-700">
                <li className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Change email back to <strong>{tokenInfo.originalEmail}</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Give you a link to change your password</span>
                </li>
                <li className="flex items-start gap-2">
                  <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Secure your account from the unauthorized change</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 mb-6">
              <div className="text-xs text-gray-500 mb-1">Currently set to:</div>
              <div className="text-sm font-medium text-gray-700">{tokenInfo.currentEmail}</div>
              <div className="text-xs text-gray-500 mt-2 mb-1">Will be reverted to:</div>
              <div className="text-sm font-medium text-green-700">{tokenInfo.originalEmail}</div>
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleRevert}
                className="w-full h-11 text-base font-semibold bg-red-600 hover:bg-red-700"
                data-testid="revert-confirm-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Yes, Revert & Secure My Account
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full h-11 text-base"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reverting state
  if (status === 'reverting') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <Card className="w-full max-w-md shadow-xl rounded-xl">
          <CardHeader className="text-center py-12">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-xl shadow-lg bg-blue-600">
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              </div>
            </div>
            <CardTitle className="text-xl font-bold text-gray-900">
              Reverting...
            </CardTitle>
            <p className="text-gray-600 mt-2">
              Restoring your original email address
            </p>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === 'success' && revertResult) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <Card className="w-full max-w-md shadow-xl rounded-xl">
          <CardHeader className="text-center pt-8 pb-4">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-xl shadow-lg bg-green-500">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-xl font-bold text-gray-900">
              Email Restored!
            </CardTitle>
            <p className="text-gray-600 mt-2">
              Your email has been reverted successfully
            </p>
          </CardHeader>

          <CardContent className="px-6 pb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">{revertResult.email}</span>
              </div>
              <p className="text-sm text-green-800">
                ✅ Your email has been restored
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">Secure Your Account</span>
              </div>
              <p className="text-sm text-amber-700 mb-3">
                We strongly recommend changing your password and enabling 2FA to prevent future unauthorized access.
              </p>
              {revertResult.suggest2FA && (
                <p className="text-xs text-amber-600">
                  💡 Consider enabling Two-Factor Authentication for extra security
                </p>
              )}
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => {
                  if (revertResult.passwordResetUrl) {
                    window.location.href = revertResult.passwordResetUrl;
                  } else {
                    navigate('/login');
                  }
                }}
                className="w-full h-11 text-base font-semibold bg-blue-600 hover:bg-blue-700"
                data-testid="change-password-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password Now
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full h-11 text-base"
              >
                Skip for Now (Not Recommended)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl rounded-xl">
        <CardHeader className="text-center pt-8 pb-4">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg bg-red-500">
              <XCircle className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-xl font-bold text-gray-900">
            Link Invalid
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {errorMessage || 'This revert link has expired or is invalid'}
          </p>
        </CardHeader>

        <CardContent className="px-6 pb-6">
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600">
              Revert links are valid for 7 days. If you need to revert an email change, please contact support.
            </p>
          </div>

          <Button
            onClick={() => navigate('/login')}
            className="w-full h-11 text-base font-semibold bg-blue-600 hover:bg-blue-700"
          >
            Back to Login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default RevertEmailChange;
